<script setup>
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selectedValues: {
    type: [String, Number, Array],
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'search', 'close']);

const { t } = useI18n();

const searchValue = defineModel('searchValue', {
  type: String,
  default: '',
});

const searchInput = ref(null);
const optionRefs = ref([]);
const activeIndex = ref(-1);

const isSelected = option => {
  if (Array.isArray(props.selectedValues)) {
    return props.selectedValues.includes(option.value);
  }
  return option.value === props.selectedValues;
};

const onInputSearch = event => {
  searchValue.value = event.target.value;
  emit('search', event.target.value);
};

const setOptionRef = (el, index) => {
  optionRefs.value[index] = el;
};

const moveActiveIndex = delta => {
  if (!props.options.length) return;
  const nextIndex = activeIndex.value + delta;
  activeIndex.value = Math.min(
    Math.max(nextIndex, 0),
    props.options.length - 1
  );
  nextTick(() => {
    optionRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' });
  });
};

const selectActiveOption = () => {
  const activeOption = props.options[activeIndex.value];
  if (activeOption) emit('select', activeOption);
};

const onKeydown = event => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveActiveIndex(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveActiveIndex(-1);
  } else if (event.key === 'Enter') {
    event.preventDefault();
    selectActiveOption();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
  }
};

watch(
  () => props.options,
  () => {
    activeIndex.value = -1;
    optionRefs.value = [];
  }
);

watch(
  () => props.open,
  isOpen => {
    if (isOpen) activeIndex.value = -1;
  }
);

defineExpose({
  focus: () => searchInput.value?.focus(),
});
</script>

<template>
  <div
    v-show="open"
    class="absolute z-50 w-full mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
  >
    <div class="relative border-b border-n-strong">
      <Spinner
        v-if="loading"
        :size="16"
        class="absolute top-2.5 start-3 text-n-slate-11"
      />
      <Icon
        v-else
        icon="i-lucide-search"
        class="absolute top-2.5 size-4 start-3"
      />
      <input
        ref="searchInput"
        :value="searchValue"
        type="search"
        :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 !ps-10 !pe-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
        @input="onInputSearch"
        @keydown="onKeydown"
      />
    </div>
    <ul
      class="py-1 mb-0 overflow-auto max-h-60"
      role="listbox"
      :aria-multiselectable="multiple"
    >
      <li
        v-for="(option, index) in options"
        :ref="el => setOptionRef(el, index)"
        :key="`${option.value}-${index}`"
        class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
        :class="{
          'bg-n-alpha-2': isSelected(option),
          'ring-1 ring-inset ring-n-brand': index === activeIndex,
        }"
        role="option"
        :aria-selected="isSelected(option)"
        @click.stop="emit('select', option)"
        @mouseenter="activeIndex = index"
      >
        <span
          :class="{
            'font-medium': isSelected(option),
          }"
          class="text-n-slate-12"
        >
          {{ option.label }}
        </span>
        <span
          v-if="isSelected(option)"
          class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
        />
      </li>
      <li v-if="options.length === 0" class="px-3 py-2 text-sm text-n-slate-11">
        {{ emptyState || t('COMBOBOX.EMPTY_STATE') }}
      </li>
    </ul>
  </div>
</template>
